<template>
  <div>
    <transition name="modal">
      <rejection-modal
        v-if="modalMode === 'reject'"
        :loading="loadingAction"
        message="Rechazar solicitud: escribe la justificación que se enviará al cliente."
        @close="closeModal"
        @confirm="onReject"
      />
    </transition>

    <transition name="modal">
      <rejection-modal
        v-if="modalMode === 'requestInfo'"
        :loading="loadingAction"
        message="Pedir más detalles: ¿qué necesitas saber del cliente antes de continuar?"
        @close="closeModal"
        @confirm="onRequestInfo"
      />
    </transition>

    <transition name="modal">
      <approve-appointment-modal
        v-if="modalMode === 'approve'"
        :loading="loadingAction"
        @close="closeModal"
        @confirm="onApprove"
      />
    </transition>

    <transition name="modal">
      <schedule-appointment-modal
        v-if="modalMode === 'schedule' || modalMode === 'reschedule'"
        :loading="loadingAction"
        :title="modalMode === 'reschedule' ? 'Reprogramar cita' : 'Registrar cita'"
        :confirm-label="modalMode === 'reschedule' ? 'Reprogramar' : 'Registrar'"
        :initial-scheduled-for="modalMode === 'reschedule' ? appointmentRequest.scheduled_for : null"
        :initial-meet-url="modalMode === 'reschedule' ? appointmentRequest.meet_url : null"
        @close="closeModal"
        @confirm="onSchedule"
      />
    </transition>

    <transition name="modal">
      <confirm-with-notes-modal
        v-if="closeConfig"
        :loading="loadingAction"
        :message="closeConfig.message"
        :notes-label="closeConfig.notesLabel"
        @close="closeModal"
        @confirm="onClose"
      />
    </transition>

    <div v-if="!loading">
      <div v-if="appointmentRequest" class="flex flex-col">
        <div class="card w-full">
          <div class="flex justify-between items-center flex-wrap gap-2">
            <p class="font-semibold">ID: #{{ appointmentRequest.id }}</p>
            <span :class="['badge', statusBadgeClass(appointmentRequest.status)]">
              <span class="badge-dot"></span> {{ statusLabel(appointmentRequest.status) }}
            </span>
          </div>

          <div class="div-line"></div>

          <div
            v-if="appointmentRequest.scheduled_for"
            class="mt-2 p-4 rounded-lg bg-primary-50 border border-primary-200"
          >
            <p class="font-semibold text-primary-700">Cita agendada</p>
            <p class="mt-1 text-sm">{{ formatScheduledFor(appointmentRequest.scheduled_for) }}</p>
            <a
              v-if="appointmentRequest.meet_url"
              :href="appointmentRequest.meet_url"
              target="_blank"
              rel="noopener"
              class="underline system-button text-sm mt-2 inline-block break-all"
            >{{ appointmentRequest.meet_url }}</a>
            <p v-else class="text-xs text-content-400 mt-2">Sin link de reunión configurado.</p>
          </div>

          <div class="mt-2 text-sm">
            <p class="font-medium">Cliente</p>
            <router-link
              :to="{ name: 'clientsDetail', params: { id: appointmentRequest.client.id } }"
              class="underline system-button"
            >{{ appointmentRequest.client.name }} {{ appointmentRequest.client.last_name }}</router-link>
            <p v-if="appointmentRequest.client.user?.email" class="text-content-300 text-xs mt-1">
              {{ appointmentRequest.client.user.email }}
            </p>
          </div>

          <div class="mt-2 text-sm">
            <p class="font-medium">Mascota</p>
            <router-link
              v-if="appointmentRequest.pet"
              :to="{ name: 'petsDetail', params: { id: appointmentRequest.pet.id } }"
              class="underline system-button"
            >{{ appointmentRequest.pet.name }}</router-link>
            <span v-else class="text-content-300">—</span>
          </div>

          <div class="mt-2 text-sm">
            <p class="font-medium">Motivo</p>
            <p class="whitespace-pre-line">{{ appointmentRequest.reason }}</p>
          </div>

          <div
            v-if="appointmentRequest.status === 'approved' && appointmentRequest.booking_url"
            class="mt-2 text-sm"
          >
            <p class="font-medium">Agenda</p>
            <a
              :href="appointmentRequest.booking_url"
              target="_blank"
              rel="noopener"
              class="underline system-button"
            >{{ appointmentRequest.booking_url }}</a>
          </div>

          <div v-if="isTerminal && appointmentRequest.responded_at" class="mt-2 text-sm">
            <p class="font-medium">Resultado</p>
            <p>{{ statusLabel(appointmentRequest.status) }}</p>
            <p class="text-content-300 text-xs mt-1">
              {{ formatDatetime(appointmentRequest.responded_at) }}
              <span v-if="respondedByAdmin">
                — {{ respondedByAdmin.name }} {{ respondedByAdmin.last_name }}
              </span>
            </p>
          </div>

          <div v-if="appointmentRequest.messages?.length" class="mt-4 text-sm">
            <p class="font-medium mb-2">Historial de mensajes</p>
            <div class="flex flex-col gap-2">
              <div
                v-for="msg in appointmentRequest.messages"
                :key="msg.id"
                :class="[
                  'max-w-[85%] rounded-lg px-3 py-2',
                  msg.sender === 'admin'
                    ? 'ml-auto bg-primary-100 text-primary-900'
                    : 'mr-auto bg-content-100 text-content-700',
                ]"
              >
                <p class="font-medium text-xs">{{ messageSenderLabel(msg) }}</p>
                <p class="whitespace-pre-line mt-0.5">{{ msg.message }}</p>
                <p class="text-[11px] text-content-400 mt-1">{{ formatDatetime(msg.created_at) }}</p>
              </div>
            </div>
          </div>

          <div class="mt-2 text-sm">
            <p class="font-medium">Fecha de creación</p>
            <p>{{ formatDatetime(appointmentRequest.created_at) }}</p>
          </div>

          <div
            v-if="hasPermission('edit_appointment_requests')"
            class="flex flex-wrap gap-2 mt-4"
          >
            <template v-if="canTriage">
              <button class="primary-button mx-0" @click="openModal('approve')">
                Aprobar y compartir agenda
              </button>

              <button
                v-if="appointmentRequest.status === 'pending'"
                class="secondary-button"
                @click="openModal('requestInfo')"
              >
                Pedir más detalles
              </button>

              <button class="delete-button" @click="openModal('reject')">
                Rechazar
              </button>
            </template>

            <template v-if="appointmentRequest.status === 'approved'">
              <button class="primary-button mx-0" @click="openModal('schedule')">
                Registrar cita
              </button>

              <button class="delete-button" @click="openModal('cancel')">
                Cancelar cita
              </button>
            </template>

            <template v-if="appointmentRequest.status === 'scheduled'">
              <button class="secondary-button" @click="openModal('reschedule')">
                Reprogramar
              </button>

              <button
                class="primary-button mx-0 bg-success-500 hover:bg-success-600"
                @click="openModal('complete')"
              >
                Finalizada
              </button>

              <button
                class="secondary-button bg-tertiary-50 text-tertiary-700 border-tertiary-300 hover:bg-tertiary-500 hover:text-white"
                @click="openModal('noShow')"
              >
                No asistió
              </button>

              <button class="delete-button" @click="openModal('cancel')">
                Cancelar cita
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import RejectionModal from "@/components/ui/RejectionModal";
import ConfirmWithNotesModal from "@/components/ui/ConfirmWithNotesModal";
import ApproveAppointmentModal from "./ApproveAppointmentModal.vue";
import ScheduleAppointmentModal from "./ScheduleAppointmentModal.vue";
import { formatDatetime } from "@/utils/dateFormatter";

const STATUS_LABELS = {
  pending: "Pendiente",
  needs_info: "Esperando cliente",
  approved: "Aprobada",
  scheduled: "Agendada",
  completed: "Finalizada",
  no_show: "No asistió",
  canceled: "Cancelada",
  rejected: "Rechazada",
};

const STATUS_BADGE_CLASSES = {
  pending: "badge-warning",
  needs_info: "badge-info",
  approved: "badge-success",
  scheduled: "badge-info",
  completed: "badge-success",
  no_show: "badge-warning",
  canceled: "badge-error",
  rejected: "badge-error",
};

const TERMINAL_STATUSES = ["completed", "no_show", "canceled", "rejected"];

const CLOSE_CONFIG = {
  complete: {
    status: "completed",
    message: "¿Confirmas que la cita fue atendida? Se marcará como Finalizada.",
    notesLabel: "Notas de la cita (opcional)",
  },
  noShow: {
    status: "no_show",
    message: "¿Confirmas que el cliente no asistió a la cita?",
    notesLabel: "Comentarios (opcional)",
  },
  cancel: {
    status: "canceled",
    message: "¿Confirmas que deseas cancelar la cita? Se notificará al cliente.",
    notesLabel: "Motivo (opcional)",
  },
};

export default {
  name: "Appointment Request Detail",

  components: {
    RejectionModal,
    ConfirmWithNotesModal,
    ApproveAppointmentModal,
    ScheduleAppointmentModal,
  },

  data() {
    return {
      appointmentRequest: null,
      loading: false,
      modalMode: null,
      loadingAction: false,
    };
  },

  computed: {
    canTriage() {
      if (!this.appointmentRequest) return false;
      return ["pending", "needs_info"].includes(this.appointmentRequest.status);
    },

    isTerminal() {
      if (!this.appointmentRequest) return false;
      return TERMINAL_STATUSES.includes(this.appointmentRequest.status);
    },

    closeConfig() {
      return CLOSE_CONFIG[this.modalMode] || null;
    },

    // `responded_by` collides with the FK column name once the relation is
    // eager-loaded (Eloquent snake-cases the relation key on serialization),
    // so it arrives as the nested User (with .admin) rather than an integer.
    respondedByAdmin() {
      return this.appointmentRequest?.responded_by?.admin || null;
    },
  },

  created() {
    this.$store.commit("setSelectedTab", "appointmentRequests");
    this.loadAppointmentRequest();
  },

  methods: {
    formatDatetime,

    statusLabel(status) {
      return STATUS_LABELS[status] || status;
    },

    statusBadgeClass(status) {
      return STATUS_BADGE_CLASSES[status] || "badge-neutral";
    },

    formatScheduledFor(datetime) {
      if (!datetime) return "";
      const date = new Date(datetime);
      if (Number.isNaN(date.getTime())) return "";
      const dateStr = date.toLocaleDateString("es-MX", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      const timeStr = date.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
      return `${dateStr.charAt(0).toUpperCase()}${dateStr.slice(1)}, ${timeStr}`;
    },

    messageSenderLabel(msg) {
      if (msg.sender === "client") return "Cliente";
      return msg.user?.admin
        ? `${msg.user.admin.name} ${msg.user.admin.last_name}`
        : "Trunimal";
    },

    async loadAppointmentRequest() {
      this.loading = true;

      try {
        this.appointmentRequest = await api.appointmentRequests.getById(
          this.$route.params.id
        );
        this.$store.commit("setBreadcrumbLabel", {
          routeName: "appointmentRequestsDetail",
          label: "Cita #" + this.appointmentRequest.id,
        });
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
        this.$router.push({ name: "appointmentRequestsIndex" });
      }

      this.loading = false;
    },

    openModal(mode) {
      this.modalMode = mode;
    },

    closeModal() {
      this.modalMode = null;
    },

    async onApprove(payload) {
      this.loadingAction = true;

      try {
        const response = await api.appointmentRequests.approve(
          this.appointmentRequest.id,
          payload
        );
        this.$store.dispatch("successSnack", response.message);
        this.closeModal();
        this.loadAppointmentRequest();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingAction = false;
    },

    async onReject(message) {
      this.loadingAction = true;

      try {
        const response = await api.appointmentRequests.reject(
          this.appointmentRequest.id,
          { message }
        );
        this.$store.dispatch("successSnack", response.message);
        this.closeModal();
        this.loadAppointmentRequest();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingAction = false;
    },

    async onRequestInfo(message) {
      this.loadingAction = true;

      try {
        const response = await api.appointmentRequests.requestInfo(
          this.appointmentRequest.id,
          { message }
        );
        this.$store.dispatch("successSnack", response.message);
        this.closeModal();
        this.loadAppointmentRequest();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingAction = false;
    },

    async onSchedule(payload) {
      this.loadingAction = true;

      try {
        const response = await api.appointmentRequests.schedule(
          this.appointmentRequest.id,
          payload
        );
        this.$store.dispatch("successSnack", response.message);
        this.closeModal();
        this.loadAppointmentRequest();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingAction = false;
    },

    async onClose(message) {
      if (!this.closeConfig) return;
      this.loadingAction = true;

      try {
        const response = await api.appointmentRequests.close(this.appointmentRequest.id, {
          status: this.closeConfig.status,
          message: message || null,
        });
        this.$store.dispatch("successSnack", response.message);
        this.closeModal();
        this.loadAppointmentRequest();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingAction = false;
    },
  },
};
</script>
